import React, { useMemo, useState } from "react";
import { Modal, Box, Typography, Divider, IconButton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";

export default function TreeMapChart({
  data = [],
  nameKey,
  sizeKey,
  getSize,
  colorResolver,
  detailRows = [],
  height = 400,
  seriesTitle,
  width = "100%",
  secondaryLabel,
  minFontSize = { desktop: 10, mobile: 8 },
  maxFontSize = { desktop: 42, mobile: 20 },
}) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const [open, setOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const validData = data.filter((item) => {
      const value = getSize ? getSize(item) : sizeKey ? item[sizeKey] : 0;
      return typeof value === "number" && value >= 0;
    });

    if (!validData.length) return [];

    const values = validData.map((item) =>
      getSize ? getSize(item) : item[sizeKey],
    );

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);

    const currentMinFont = isMobile
      ? (minFontSize.mobile ?? 4)
      : (minFontSize.desktop ?? 10);
    const currentMaxFont = isMobile
      ? (maxFontSize.mobile ?? 14)
      : (maxFontSize.desktop ?? 42);

    return validData.map((item) => {
      let value = getSize ? getSize(item) : item[sizeKey];
      if (maxVal === 0 && minVal === 0) value = 1;

      let dynamicFontSize;

      if (maxVal === 0 && minVal === 0) {
        // All values are zero → use minFontSize for everything
        dynamicFontSize = currentMinFont;
      } else if (maxVal > minVal) {
        // Normal proportional scaling
        const ratio = (value - minVal) / (maxVal - minVal);
        dynamicFontSize =
          currentMinFont + ratio * (currentMaxFont - currentMinFont);
      } else {
        // maxVal === minVal but not zero → avoid division by zero
        dynamicFontSize = currentMinFont;
      }

      dynamicFontSize = Math.round(dynamicFontSize);

      dynamicFontSize = Math.round(dynamicFontSize);

      let displayName = String(item[nameKey]);
      if (secondaryLabel) {
        const secondaryValue = secondaryLabel.formatter
          ? secondaryLabel.formatter(item)
          : item[secondaryLabel.key];

        if (secondaryValue != null && secondaryValue !== "") {
          const ltrNumber = `\u200E${secondaryValue}`;
          displayName += ` (${ltrNumber})`;
        }
      }

      return {
        name: displayName,
        value,
        itemStyle: {
          color: colorResolver ? colorResolver(item) : "#8d8f91",
          borderRadius: 8,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          color: "#000",
          fontSize: Math.round(dynamicFontSize),
          fontWeight: "bold",
          fontFamily: "IRANSansWeb_Farsi",
          align: "center",
          verticalAlign: "middle",
        },
        customData: item,
      };
    });
  }, [data, sizeKey, getSize, nameKey, colorResolver, secondaryLabel]);

  const hasData = chartData.length > 0;

  const handleClick = (params) => {
    if (isMobile && params?.data?.customData) {
      setTimeout(() => {
        setSelectedData(params.data.customData);
        setOpen(true);
      }, 100);
    }
  };

  const getOption = () => ({
    textStyle: {
      fontFamily: "IRANSansWeb_Farsi",
    },
    tooltip: isMobile
      ? { show: false }
      : {
          trigger: "item",
          triggerOn: "mousemove|click",
          confine: true,
          enterable: false,
          transitionDuration: 0,
          formatter: (params) => {
            const item = params.data?.customData;
            if (!item) return "";

            return `
          <div style="min-width: 250px; padding: 10px; font-family: IRANSansWeb_Farsi; direction: rtl; text-align: right;">
            <div style="font-weight: bold; text-align: center; margin-bottom: 10px; border-bottom: 1px solid #eee; padding-bottom: 5px;">
                  ${item[nameKey]}
                </div>
                ${detailRows
                  .map((row) =>
                    renderRow(
                      row.label,
                      row.formatter
                        ? row.formatter(item)
                        : String(item[row.key] ?? "-"),
                      row.colorResolver ? row.colorResolver(item) : "black",
                    ),
                  )
                  .join("")}
              </div>
            `;
          },
          backgroundColor: "#fff",
          textStyle: { color: "#000", fontFamily: "IRANSansWeb_Farsi" },
          extraCssText:
            "box-shadow:0 0 10px rgba(0,0,0,0.2);border-radius:8px;",
        },
    series: [
      {
        type: "treemap",
        data: chartData,
        name: seriesTitle,
        left: "center",
        right: "center",
        width: width,
        height: "85%",
        top: "5%",
        roam: isMobile ? false : true,
        nodeClick: "zoomToNode",
        breadcrumb: {
          show: true,
          height: 35,
          bottom: 0,
          textStyle: {
            color: "#333",
            fontFamily: "IRANSansWeb_Farsi",
            fontSize: 14,
          },
        },
        label: {
          show: true,
          position: "inside",
          align: "center",
          verticalAlign: "middle",
          formatter: "{b}",
          fontFamily: "IRANSansWeb_Farsi",
        },

        itemStyle: {
          borderColor: "#fff",
          borderWidth: 2,
          gapWidth: 2,
          borderRadius: 8,
        },
      },
    ],
  });

  function renderRow(label, value, valueColor = "black") {
    return `
      <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px;">
        <span style="font-weight: bold; color: #555;">${label}</span>
        <span style="color: ${valueColor}; direction: ltr;">${value}</span>
      </div>
    `;
  }

  const DataRow = ({ label, value, icon, color = "text.primary" }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1.5,
        borderRadius: 2,
        bgcolor: "#f9f9f9",
        mb: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color, direction: "ltr" }}
      >
        {value}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <div
      style={{
        height,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {hasData ? (
        <>
          <ReactECharts
            option={getOption()}
            style={{ height: "100%", width: "100%" }}
            notMerge
            onEvents={{ click: handleClick }}
          />

          <AnimatePresence>
            {open && (
              <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition={false}
                slotProps={{
                  backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.4)" } },
                }}
              >
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 130000,
                    outline: "none",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: "24px 24px 0 0",
                      p: 2,
                      direction: "rtl",
                      maxHeight: "80vh",
                      boxShadow: "0px -4px 20px rgba(0,0,0,0.1)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 4,
                        bgcolor: "#ddd",
                        borderRadius: 2,
                        mx: "auto",
                        mb: 2,
                      }}
                    />

                    {selectedData && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <IconButton
                            onClick={() => setOpen(false)}
                            sx={{ mt: -1 }}
                          >
                            <CloseIcon />
                          </IconButton>

                          <Box sx={{ textAlign: "left", flex: 1 }}>
                            <Typography variant="h6" fontWeight={900}>
                              {selectedData[nameKey]}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {detailRows.map((row, index) => (
                          <DataRow
                            key={index}
                            label={row.label}
                            value={
                              row.formatter
                                ? row.formatter(selectedData)
                                : String(selectedData[row.key] ?? "-")
                            }
                            color={
                              row.colorResolver
                                ? row.colorResolver(selectedData)
                                : "text.primary"
                            }
                          />
                        ))}
                      </>
                    )}
                  </Box>
                </motion.div>
              </Modal>
            )}
          </AnimatePresence>
        </>
      ) : (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            bgcolor: "#fafafa",
            borderRadius: 3,
            border: "1px dashed #e0e0e0",
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              bgcolor: "#f0f0f0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 28,
            }}
          >
            📊
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            متاسفانه اطلاعاتی برای نمایش وجود ندارد
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "text.disabled", textAlign: "center" }}
          >
            لطفاً بعداً دوباره تلاش کنید
          </Typography>
        </Box>
      )}
    </div>
  );
}
